import React, { useCallback, useEffect, useState } from 'react';
import statementService from '../../services/statementService';
import { useT, useLocale } from '../../i18n/LanguageContext';
import { money, whenLabel, stampLabel } from './formatters';
import { unwrap } from './responseSafety';
import { refusalLabel } from './runPresentation';
import RunDetail, { RunModeBadge } from './components/RunDetail';
import Skeleton from './components/Skeleton';
import Banner from './components/Banner';
import { Card, ErrorState, Gate, Stat, useScanLoad } from './tabShared';

const fetchPlan = async () => unwrap(await statementService.getRunPlan(), 'a run plan');

export default function RulesTab() {
  const t = useT();
  const locale = useLocale();
  // The plan is the page. History is supporting detail, and a deployment that has never run has
  // none - so a failure to read it must not blank the status the admin actually came for.
  const {
    data: plan,
    loading,
    error,
    refreshError,
    refreshing,
    reload: load,
  } = useScanLoad(fetchPlan, 'Could not read the sending status.');

  /**
   * THE RUN HISTORY IS READ SEPARATELY, AND CHEAPLY.
   *
   * /runs is a Mongo query with no throttle. /run-plan walks the entire QuickBooks company file
   * behind a one-request-a-minute cap, against a per-realm Intuit limit that live order invoicing
   * shares. Loading them together meant the Refresh button sitting on the run-history card paid for
   * a full company-file walk - and, because the plan is awaited first, usually 429'd before it ever
   * reached the history the operator had actually asked to refresh.
   */
  const [runs, setRuns] = useState(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      setRuns(unwrap(await statementService.listRuns(10), 'a run history').runs || []);
    } catch {
      setRuns(null);
    } finally {
      setRunsLoading(false);
    }
  }, []);
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const [openRun, setOpenRun] = useState(null);

  if (loading) return <Skeleton rows={6} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!plan) return null;

  const schedule = plan.schedule || {};
  const transport = plan.transport || {};
  const isProd = plan.environment === 'production';
  const next = whenLabel(schedule.nextRunAt, schedule.timezone, locale);

  /**
   * "ON" MEANS EVERY GATE, NOT JUST THE TWO SWITCHES.
   *
   * `schedule.unattended` is only the two environment variables. A deployment with both armed, no
   * connected mailbox and a sandbox ledger would have shown "Statements send automatically every
   * week" while the run aborts every Tuesday - the single most dangerous thing this page can say.
   * The switches, transport, ledger and server readiness checks all contribute to the headline.
   * Missing checks or a failed refresh cannot certify a currently armed schedule as on or off.
   */
  const verified = plan.readiness?.validated === true && !refreshError;
  const inputsReady = verified && plan.readiness?.ready === true;
  const armed =
    plan.sendingEnabled === true && schedule.armed === true && schedule.unattended === true;
  const live = armed && transport.ready === true && isProd && inputsReady;
  const unknown = armed && !verified;
  const readinessLabels = {
    statement_date_not_today: 'Refresh the plan for today before sending.',
    production_ledger_required: 'The live QuickBooks company file is required.',
    statement_list_truncated: 'The customer list is incomplete. Sending is blocked.',
    invoice_due_dates_missing: 'Invoice due dates are missing. Sending is blocked.',
    delivery_index_unavailable: 'Duplicate prevention could not be verified. Sending is blocked.',
  };

  /**
   * How many customers the TUESDAY RUN would email.
   *
   * Not `plan.wouldSend`: that applies the caller's confirmation gate, which a GET can never carry,
   * so it is structurally always 0 here. Reporting it would tell a fully-armed deployment it was
   * about to email nobody.
   */
  // `plan.eligible` is the fallback because it predates this field: an admin app deployed ahead of
  // the API would otherwise read `undefined`, print 0, and reproduce the exact sentence this
  // replaced. The two deploy independently, so the skew window is real rather than theoretical.
  const willEmail = schedule.wouldSendUnattended ?? plan.eligible ?? 0;

  // NAMED, not just confirmed: which mailbox is the fact an operator needs before letting
  // statements go out.
  const mailboxDetail = transport.mailbox
    ? t('Connected as {mailbox}.', { mailbox: transport.mailbox })
    : t('Connected.');

  return (
    <div className="space-y-5">
      {/* A REFRESH THAT FAILED HAS TO SAY SO.
          This was computed and never rendered, so a failed Refresh changed nothing on screen at
          all: no skeleton, no message, the same figures as before. Since /run-plan is capped at one
          request a minute, clicking Refresh twice is the ORDINARY case, and the operator was left
          reading a stale sending status as though it were current - on the one panel whose whole
          job is to say whether customers are being emailed. */}
      {refreshError && <Banner tone="warn">{t(refreshError)}</Banner>}

      {/* AT THE TOP, BESIDE THE STATUS IT REFRESHES - and beside the banner that reports its
          failure. The only refresh control used to live in the run-history card at the foot of a
          four-card panel, so on a full-height plan the operator clicked at the bottom and the
          answer appeared off-screen at the top. */}
      <div className="flex justify-end -mb-2">
        <button
          onClick={load}
          disabled={refreshing}
          aria-label={t('Refresh the sending status')}
          className="text-[13px] text-[#0089ff] hover:underline disabled:opacity-50 disabled:no-underline"
        >
          {refreshing ? t('Re-reading the company file...') : t('Refresh')}
        </button>
      </div>

      {/* The environment is stated on the page, never inferred. A report that does not say which
          QuickBooks company it read is the one mistake worth guarding against loudly.
          Warns unless the server positively says production. An equality test against 'sandbox'
          fails OPEN: any drift - a renamed value, a missing field, a stale payload - silently drops
          the warning, and the failure mode is "looks like the live company file". */}
      {!isProd && (
        <Banner tone="warn">
          {t('These numbers did not come from the live QuickBooks company file')}
          {' ('}
          {plan.environment
            ? t('environment: {environment}', { environment: plan.environment })
            : t('environment unknown')}
          {'). '}
          {t('A live run refuses to send from anything but the production ledger.')}
        </Banner>
      )}

      <div
        className={`rounded-xl border px-5 py-4 flex items-start gap-4 ${
          live ? 'bg-[#f0fdf4] border-[#bbf7d0]' : 'bg-[#f8fafc] border-[#e2e8f0]'
        }`}
      >
        <span
          aria-hidden="true"
          className={`material-symbols-outlined text-[28px] ${
            live ? 'text-[#16a34a]' : 'text-[#64748b]'
          }`}
        >
          {live ? 'mark_email_read' : 'pause_circle'}
        </span>
        <div className="min-w-0">
          <div className="text-[17px] font-bold text-[#0f172a]">
            {live
              ? t('Statements send automatically every week')
              : unknown
                ? t('Automatic sending status needs verification')
                : t('Statements are not being sent automatically')}
          </div>
          <p className="text-[13px] text-[#475569] mt-1">
            {live ? (
              <>
                {t('The next run is')} <strong>{next || t('not known')}</strong>
                {t(', and it will email')} <strong>{willEmail}</strong>{' '}
                {t(willEmail === 1 ? 'customer' : 'customers')}.
              </>
            ) : unknown ? (
              t(
                'Refresh the sending status to verify the current checks. This screen cannot confirm whether the next run will send.'
              )
            ) : (
              <>
                {t('Nothing will be emailed to anyone. The weekly slot is')}{' '}
                {next ? <strong>{next}</strong> : t('every Tuesday at noon Central')}
                {t('; if everything below were ready,')} <strong>{plan.eligible ?? 0}</strong>{' '}
                {t(
                  plan.eligible === 1
                    ? 'customer would receive a statement.'
                    : 'customers would receive a statement.'
                )}
              </>
            )}
          </p>
        </div>
      </div>

      {/* THE ONE INPUT CONDITION THAT ABORTS EVERY RUN. The server reports it precisely so the
          approval screen and the thing it approves cannot disagree - and without it the page can
          show four green ticks and "sends automatically every week" while the Tuesday run refuses
          to start, every week, because the customer list came back capped. */}
      {plan.lineTruncated?.count > 0 && (
        <Banner tone="warn">
          {t(
            plan.lineTruncated.count === 1
              ? '{count} customer has more open documents than fit on a statement.'
              : '{count} customers have more open documents than fit on a statement.',
            { count: plan.lineTruncated.count }
          )}{' '}
          {t(
            'Their total is correct, but the rows printed beneath it will not add up to it - the document says so, and their accounts-payable clerk will ask.'
          )}
          {plan.lineTruncated.customers?.length > 0 && (
            <>
              <ul className="mt-2 list-disc pl-5">
                {plan.lineTruncated.customers.map((c, index) => (
                  <li key={`${c.qbCustomerId || c.customerId || c.name}-${index}`}>
                    {c.name} -{' '}
                    {t('showing {shown} of {total} open documents', {
                      shown: c.shown,
                      total: c.openDocuments,
                    })}
                  </li>
                ))}
              </ul>
              {/* The server names at most 15 of them. Printing the full count and then a short list
                  with nothing between the two reads as the whole list, and the customers missing
                  from it are the ones nobody goes looking for. */}
              {plan.lineTruncated.count > plan.lineTruncated.customers.length && (
                <p className="mt-1">
                  {t('{shown} of {count} are named here.', {
                    shown: plan.lineTruncated.customers.length,
                    count: plan.lineTruncated.count,
                  })}
                </p>
              )}
            </>
          )}
        </Banner>
      )}

      {/* THE OTHER INPUT CONDITION THAT ABORTS A LIVE RUN, and it was reported on the Recipients
          tab only. The run refuses to start when every line was aged from its transaction date,
          because that is a broken read rather than real data - so this panel could show four green
          ticks, "Statements send automatically every week" and a recipient count, while the Tuesday
          run aborted on an input the operator was never shown. */}
      {plan.linesAgedFromTxnDate?.count > 0 &&
        plan.linesAgedFromTxnDate.count === plan.linesAgedFromTxnDate.ofTotalLines && (
          <Banner tone="error">
            {t(
              'Every invoice was aged from its transaction date because none carried a due date. That is usually a broken read, not real data, and a live run refuses to start on it - nothing will be sent until it is resolved.'
            )}
          </Banner>
        )}

      {plan.truncated && (
        <Banner tone="error">
          {t(
            'The customer list came back capped, so some customers are missing from it. A run refuses to start in this state rather than quietly leaving the same customers out every week - nothing will be sent until it is resolved.'
          )}
        </Banner>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label={t('Would be emailed')}
          value={unknown ? '--' : live ? willEmail : 0}
          hint={
            unknown
              ? t('Sending status is unverified')
              : live
                ? t('on the next run')
                : t('blocked - see below')
          }
          tone={!live && (plan.eligible ?? 0) > 0 ? 'warn' : undefined}
        />
        <Stat
          label={t('Eligible customers')}
          value={plan.eligible ?? 0}
          // NOT "with a balance". `candidates` counts the statements that were BUILT, which is
          // already after the ledger read drops sub-customers, non-practices, inactive accounts,
          // customers with no billing email and anything not in dollars. On a real company file -
          // which mixes suppliers and bank-feed artifacts in with the clinics - that is materially
          // fewer than the number of customers who owe money.
          hint={t('of {count} with a statement', { count: plan.candidates ?? 0 })}
        />
        <Stat
          label={t('Cannot be sent')}
          value={Math.max((plan.candidates ?? 0) - (plan.eligible ?? 0), 0)}
          hint={t('reasons listed below')}
          tone={(plan.candidates ?? 0) - (plan.eligible ?? 0) > 0 ? 'warn' : undefined}
        />
        <Stat
          label={t('Billing week')}
          value={plan.weekOf || '--'}
          hint={t('as of {date}', { date: plan.asOf || '--' })}
        />
      </div>

      {/* WHO ACTUALLY RECEIVES ONE.
          This is the approval screen, and until now it answered every question about the send
          except the first one anybody asks: who is it going to? It reported a count and then listed
          only the customers being held BACK - so an operator could approve a weekly run without the
          page ever naming a single person who would be emailed, or a single address it would be
          emailed to. The server has returned this list all along. */}
      <Card
        // WORDED FOR THE STATE THE PAGE IS ACTUALLY IN.
        //
        // `recipients` is who is eligible ON THEIR OWN MERITS - the deployment gates are
        // deliberately excluded from it server-side. Sending is off on every deployment today, so
        // the unconditional heading "Who will receive a statement" sat eight lines under "Nothing
        // will be emailed to anyone" and beside a tile reading 0, naming customers and real billing
        // addresses under a promise the same panel had just denied. The list is the right list; the
        // sentence around it has to say which question it answers.
        title={live ? t('Who will receive a statement') : t('Who would receive a statement')}
        subtitle={`${
          live
            ? t('Each statement goes to the listed address and any CC recipients shown here.')
            : unknown
              ? t('These are the last reported eligible customers. Refresh to verify the next run.')
              : t(
                  'Nothing is being emailed. This is who would be contacted, at these addresses, if every condition below were met.'
                )
        }${
          schedule.weekOf
            ? ` ${t('Counted for the run in the week of {weekOf}.', { weekOf: schedule.weekOf })}`
            : ''
        }`}
      >
        {(plan.recipients || []).length === 0 ? (
          <p className="px-5 py-6 text-sm text-[#64748b]">
            {t('No customer is eligible to receive a statement.')}
          </p>
        ) : (
          // Focusable, because it scrolls and holds nothing focusable of its own: without a tab
          // stop a keyboard-only user cannot read past the first handful of recipients.
          <div
            className="max-h-[320px] overflow-y-auto overflow-x-auto"
            tabIndex={0}
            role="group"
            aria-label={t('Customers who would receive a statement')}
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[12px] text-[#64748b] border-b border-[#e2e8f0]">
                  <th className="px-5 py-3 font-medium">{t('Customer')}</th>
                  <th className="px-5 py-3 font-medium">{t('Statement goes to')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('Amount due')}</th>
                </tr>
              </thead>
              <tbody>
                {plan.recipients.map((r) => (
                  <tr
                    key={r.qbCustomerId || r.name}
                    className="border-b border-[#f1f5f9] last:border-0"
                  >
                    <td className="px-5 py-2.5 text-[#0f172a]">{r.name}</td>
                    {/* The address is the whole point of this table, so it is never abbreviated
                        and never left blank - an eligible recipient with no address would be a
                        contradiction worth seeing rather than an empty cell. */}
                    <td className="px-5 py-2.5 text-[#475569] break-all">
                      <div>{r.email || '--'}</div>
                      {Array.isArray(r.statementCcEmails) && r.statementCcEmails.length > 0 && (
                        <div className="mt-1 text-xs text-[#64748b]">
                          {t('CC')}: {r.statementCcEmails.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums">
                      {money(r.totalOpen, r.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* The server caps this list at 500 and says so in its own field. Printing 500 names under
            a heading that promises the recipients, while the tile above counts 900, is exactly the
            kind of quiet disagreement the rest of this page refuses to make. */}
        {plan.recipientsTruncated && (
          <p className="px-5 py-3 text-[12px] text-[#005fb8] border-t border-[#f1f5f9]">
            {t('Showing the first {count}. More customers are eligible than are listed here.', {
              count: (plan.recipients || []).length,
            })}
          </p>
        )}
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card
          title={t('What has to be true before anything is emailed')}
          subtitle={t(
            'Every one of these must be ready. Any single one being off means no customer is contacted.'
          )}
        >
          <ul className="px-5 py-2 divide-y divide-[#f1f5f9]">
            <Gate
              ok={Boolean(plan.sendingEnabled)}
              label="Sending is switched on for this deployment"
              detail={plan.gates?.killSwitch}
            />
            <Gate
              ok={Boolean(schedule.armed)}
              label="The weekly schedule is armed"
              detail={plan.gates?.schedule}
            />
            <Gate
              ok={Boolean(transport.ready)}
              label="A mailbox is connected"
              detail={
                transport.ready
                  ? mailboxDetail
                  : plan.gates?.transport || 'No mailbox is connected.'
              }
            />
            <Gate
              ok={isProd}
              label="Reading the live QuickBooks company file"
              detail={
                isProd
                  ? 'Statements are built from the production ledger.'
                  : t('Reading the {environment} company file. A live run refuses this.', {
                      environment: plan.environment || t('unknown'),
                    })
              }
            />
            <Gate
              ok={inputsReady}
              label="Statement data and duplicate prevention are verified"
              detail={
                inputsReady
                  ? 'The statement date, complete customer list, invoice due dates and delivery index passed their checks.'
                  : 'Refresh the sending status and resolve the reported checks before enabling statements.'
              }
            />
            {(plan.readiness?.blockers || []).map((code, index) => (
              <li key={index} className="py-3 text-sm text-[#475569]">
                {t(
                  readinessLabels[code] ||
                    'A statement readiness check needs attention. Sending is blocked.'
                )}
              </li>
            ))}
          </ul>
        </Card>

        <Card
          title={t('Who cannot be sent a statement')}
          subtitle={t(
            'These customers have an open balance and are being held back. Nothing goes to them.'
          )}
        >
          {(plan.refused || []).length === 0 ? (
            <p className="px-5 py-6 text-sm text-[#64748b]">
              {t(
                'Nobody is being held back - every customer with a balance can be sent a statement.'
              )}
            </p>
          ) : (
            <ul
              className="px-5 py-2 divide-y divide-[#f1f5f9] max-h-[320px] overflow-y-auto"
              tabIndex={0}
              aria-label={t('Customers who cannot be sent a statement')}
            >
              {plan.refused.map((r) => (
                <li key={r.qbCustomerId || r.name} className="py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[13px] font-medium text-[#0f172a]">{r.name}</span>
                    <span className="text-[13px] text-[#475569] tabular-nums">
                      {money(r.totalOpen, r.currency)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(r.refusals || []).map((why) => (
                      <span
                        key={why}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-[#e6f1ff] text-[#005fb8]"
                      >
                        {t(refusalLabel(why))}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
              {/* The server caps this list at 100, and the real total is already on the page:
                  candidates minus eligible is exactly the number being held back. Testing
                  `length >= 100` instead announced "more than are listed" for a plan with exactly
                  100 refusals and nothing dropped - a hardcoded mirror of a server constant making
                  a claim the server had not made. The sibling notes all compare against a real
                  total; this one now does too. */}
              {(plan.candidates ?? 0) - (plan.eligible ?? 0) > plan.refused.length && (
                <li className="py-2.5 text-[12px] text-[#005fb8]">
                  {t(
                    'Showing the first {count}. More customers are being held back than are listed here.',
                    { count: plan.refused.length }
                  )}
                </li>
              )}
            </ul>
          )}
        </Card>
      </div>

      <Card
        title={t('Run history')}
        subtitle={t(
          'Every run, including rehearsals. A rehearsal leaves the same record a real send does.'
        )}
        right={
          // `loadRuns`, not `load`: this reads Mongo and costs nothing. Wired to the tab-wide loader
          // it charged a full company-file walk for a list of five rows, and usually failed on the
          // rate limit before reaching them.
          <button
            onClick={loadRuns}
            disabled={runsLoading}
            aria-label={t('Refresh the run history')}
            className="text-[13px] text-[#0089ff] hover:underline whitespace-nowrap disabled:opacity-50 disabled:no-underline"
          >
            {runsLoading ? t('Refreshing...') : t('Refresh')}
          </button>
        }
      >
        {runs === null ? (
          <p className="px-5 py-6 text-sm text-[#64748b]">
            {t('The run history could not be read. The status above is still current.')}
          </p>
        ) : runs.length === 0 ? (
          <p className="px-5 py-6 text-sm text-[#64748b]">
            {t('No run has ever happened on this deployment.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[12px] text-[#64748b] border-b border-[#e2e8f0]">
                  <th className="px-5 py-3 font-medium">{t('Started')}</th>
                  <th className="px-5 py-3 font-medium">{t('Week')}</th>
                  <th className="px-5 py-3 font-medium">{t('Type')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('Statements')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('Failed')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('Held back')}</th>
                  <th className="px-5 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.runId} className="border-b border-[#f1f5f9] last:border-0">
                    {/* `createdAt`, not `startedAt`: the run document is written when the run
                        begins, and there is no `startedAt` field anywhere in the schema - the read
                        only ever worked because of the fallback beside it. */}
                    <td className="px-5 py-3 text-[#0f172a]">{stampLabel(r.createdAt)}</td>
                    <td className="px-5 py-3 text-[#475569]">{r.weekOf || '--'}</td>
                    <td className="px-5 py-3">
                      <RunModeBadge run={r} />
                    </td>
                    {/* A REHEARSAL RECORDS `simulated`, NOT `sent`, so reading `sent` showed 0 for
                        every run on every deployment where sending is off - which is all of them
                        today. A run that rehearsed 37 statements displayed as a quiet week. */}
                    <td className="px-5 py-3 text-right tabular-nums">
                      {r.mode === 'live' ? (r.sent ?? 0) : (r.simulated ?? 0)}
                    </td>
                    <td
                      className={`px-5 py-3 text-right tabular-nums ${
                        (r.failed ?? 0) > 0 ? 'text-[#f23e41] font-semibold' : ''
                      }`}
                    >
                      {r.failed ?? 0}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{r.skipped ?? 0}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => setOpenRun(r.runId)}
                        className="text-[#0089ff] hover:underline"
                      >
                        {t('Details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openRun && <RunDetail runId={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
}

// =================================================================================================
// Recipients
// =================================================================================================
